import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  useToast,
  Flex,
  Text,
  Box,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { useLocation, useHistory } from 'react-router-dom';
import { MdArrowForward } from 'react-icons/md';
import TicketChatApi from 'api/ticketChat';
import TicketApi from 'api/ticket';
import { IoEllipsisVertical } from 'react-icons/io5';
import Card from 'components/card/Card';
import moment from 'moment';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';
import PageError from 'components/nopermission/PageError';

export default function ViewDetails() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToast();
  const [disabled, setDisabled] = useState(state?.data?.id);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ticketDetails, setTicketDetails] = useState({});
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const { getPermissionsForModule } = usePermissions();
  const [permissions, setPermissions] = useState(null);
  const [getDataByIdError, setGetDataByIdError] = useState(false);

  const id = IdFromUrl(window.location.href);
  function IdFromUrl(url) {
    // Split the URL by slashes
    const parts = url.split('/');

    // The last part of the URL is the ID
    const id = parts[parts.length - 1];

    return id;
  }

  useEffect(() => {
    const permissions = getPermissionsForModule([Modules.CustomerTicket]);
    setPermissions(permissions);
  }, []);

  const getDatabyId = async (id) => {
    if (!isValidUUID(id)) {
      setGetDataByIdError(true);
      return;
    }
    try {
      const response = await TicketChatApi.getById(id);
      setGetDataByIdError(false);
      const ticketData = response.data.data;
      setTicketDetails(ticketData);
      setStatus(ticketData.status);
      setPriority(ticketData.priority);
      setCurrentUserId(ticketData.user_id);
      setMessages(ticketData.chat || []);
    } catch (err) {
      setGetDataByIdError(true);
      console.log(err);
      toast({
        title: 'Error fetching ticket details',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const GoToCustomerUser = (id) => {
    const url = `/admin/customeruser/${id}`;
    window.open(url, '_blank');
  };

  // const id = state?.ticketId;


  useEffect(() => {
    if (id) {
      getDatabyId(id);
    } else {
      history.push('/admin/ticketlist');
    }
  }, [id]);

  const handleSendMessage = async () => {
    try {
      const response = await TicketChatApi.SendMessage({
        ticketId: id,
        message: newMessage,
        public: true,
      });
      setMessages((prevMessages) => [...prevMessages, response.data.data]);
      setNewMessage('');
    } catch (err) {
      console.log(err);
      toast({
        title: 'Error sending message',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleChange = (e) => {
    setNewMessage(e.target.value);
  };

  const updateStatus = async (newStatus) => {
    try {
      await TicketApi.Update({ id, status: newStatus });
      setStatus(newStatus);
      toast({
        title: `Status updated to ${newStatus}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (err) {
      console.log(err);
      toast({
        title: 'Error updating status',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const updatePriority = async (newPriority) => {
    try {
      await TicketApi.Update({ id, priority: newPriority });
      setPriority(newPriority);
      toast({
        title: `Priority updated to ${newPriority}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (err) {
      console.log(err);
      toast({
        title: 'Error updating priority',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  if (!permissions?.read) {
    return <NoPermissionsMessage module={Modules.CustomerTicket} />;
  }
  return getDataByIdError ? (
    <PageError />
  ) : (
    <Flex p={5} direction='column' w='100%'>
      <Flex alignItems='center' mb={20}>
        <Text fontSize='2xl'>Ticket Details</Text>
      </Flex>
      <Flex direction='row' mb={10} justifyContent='space-between'>
        <Box position='relative' w='100%'>
          <Card>
            <Flex p={5} direction='column' h='100%' w='100%'>
              <Box position='absolute' top={2} right={2}>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<IoEllipsisVertical />}
                    variant='outline'
                    isDisabled={!permissions?.update}
                  />
                  <MenuList>
                    {status === 'OPEN' && (
                      <MenuItem onClick={() => updateStatus('INPROGRESS')}>
                        Mark as In Progress
                      </MenuItem>
                    )}
                    {status === 'INPROGRESS' && (
                      <MenuItem onClick={() => updateStatus('RESOLVED')}>
                        Mark as Resolved
                      </MenuItem>
                    )}
                    {(status === 'RESOLVED' || status === 'INPROGRESS') && (
                      <MenuItem onClick={() => updateStatus('CLOSED')}>
                        Close Ticket
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => updatePriority('LOW')}>
                      Set Priority: Low
                    </MenuItem>
                    <MenuItem onClick={() => updatePriority('NORMAL')}>
                      Set Priority: Normal
                    </MenuItem>
                    <MenuItem onClick={() => updatePriority('HIGH')}>
                      Set Priority: High
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>
              <Flex direction='column'>
                <Flex>
                  <Text fontWeight='bold'>Request Number: </Text>
                  <Text ml={2}>{ticketDetails.code}</Text>
                </Flex>
                <Flex>
                  <Text fontWeight='bold'>Subject: </Text>
                  <Text ml={2}>{ticketDetails.subject}</Text>
                </Flex>
                <Flex noOfLines={10}>
                  <Text fontWeight='bold'>Message: </Text>
                  <Text ml={2}>{ticketDetails.message}</Text>
                </Flex>
                <Flex>
                  <Text fontWeight='bold'>Status: </Text>
                  <Text ml={2}>{status}</Text>
                </Flex>
                <Flex>
                  <Text fontWeight='bold'>Priority: </Text>
                  <Text ml={2}>{priority}</Text>
                </Flex>
                <Button
                  variant='light'
                  onClick={() => {
                    GoToCustomerUser(ticketDetails.user_id);
                  }}
                >
                  <Text fontWeight='bold' mr={2}>
                    User Details
                  </Text>
                  <MdArrowForward />
                </Button>
              </Flex>
            </Flex>
          </Card>
        </Box>
      </Flex>
      <Divider />
      <Box p={5} shadow='md' borderWidth='1px'>
        <Text fontSize='xl' mb={3}>
          Chat
        </Text>

        <VStack spacing={3} align='stretch'>
          {messages
            .slice()
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .map((message, index) => (
              <Flex
                key={index}
                p={3}
                borderRadius='md'
                bg={
                  message.created_by === currentUserId ? 'blue.100' : 'gray.100'
                }
              >
                <Box>
                  <Text>{message.message}</Text>
                  <Text fontSize='xs' color='gray.500'>
                    {moment(message.created_at).format('DD MMM YY, HH:mm')}{' '}
                    {message.created_by === currentUserId
                      ? 'by Customer'
                      : 'by Admin'}
                  </Text>
                </Box>
              </Flex>
            ))}
        </VStack>
        <Divider />
        <Flex mt={5} w='100%'>
          <Input
            placeholder='Type your message here...'
            value={newMessage}
            onChange={handleChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                handleSendMessage();
                e.preventDefault();
              }
            }}
            isDisabled={status === 'CLOSED' || !permissions?.update}
          />
          <Button
            onClick={handleSendMessage}
            colorScheme='blue'
            isDisabled={
              status === 'CLOSED' ||
              newMessage.trim().length === 0 ||
              !permissions?.update
            }
          >
            Send
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}
